/**
 * The partner ledger's admin actions (TMMPDM-0095, TMMAPI-0162 §H item 24): hold for cause, release,
 * void, record a manual payment and post an adjustment. Each is a confirmed, audited tmm-api action;
 * each dialog keeps its own errors inside it (TMM-B4), in catalog words (TMM-B3).
 *
 * Money safety: an amount is exactly what the person typed (`exactMinorFromMajor`, never rounded), in
 * the statement's or the payee's currency, with the sign the person chose for an adjustment. `paidOn`
 * starts empty and is required. The console computes no bound: tmm-api checks the manual payment's
 * amount and says so by field. Void sends the partner's name as typed, as `confirmation`.
 */
import { useId, useState, type ReactNode } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useCopy } from '@/hooks/useCopy';
import { SubmitButton } from '@/components/page';
import { LedgerMoney } from './ledgerShared';
import { adminApi } from '@/api/tmm/admin';
import { ApiError, errorCopy, isValidationFailed, validationFields } from '@/api/errors';
import { exactMinorFromMajor, formatMoney } from '@/format';
import type { LedgerHoldView, LedgerStatementView } from '@/api/tmm/types';
import { isNotDeployed, periodLabel, type Translate } from './ledger';
import { HoldKindName } from './ledgerShared';

/** A token no catalog line contains: the hold's kind is swapped in around it, so word order stays the copy's. */
const KIND_TOKEN = '\u0000';

const REASON_MIN = 3;
const REASON_MAX = 500;
const REFERENCE_MAX = 200;

/** The ledger's words for a failed action: a 404 is "not available yet", else the code or `fallback`. */
function actionError(error: unknown, t: Translate, fallback: string): string {
  if (isNotDeployed(error))
    return t('ledger.notAvailable', "The partner ledger isn't available yet.");
  return errorCopy(error, t, fallback);
}

/** The field errors without one field's (it was just edited). */
function without<K extends string>(
  errors: Partial<Record<K, string>>,
  key: K,
): Partial<Record<K, string>> {
  const rest = { ...errors };
  delete rest[key];
  return rest;
}

function reasonProblem(reason: string, t: Translate): string | null {
  const trimmed = reason.trim();
  return trimmed.length < REASON_MIN || trimmed.length > REASON_MAX
    ? t('ledger.reason.length', 'Use 3 to 500 characters.')
    : null;
}

function ReasonField({
  value,
  onChange,
  error,
}: {
  value: string;
  onChange: (value: string) => void;
  error: string | null;
}) {
  const { t } = useCopy();
  return (
    <TextField
      fullWidth
      required
      multiline
      minRows={2}
      label={t('ledger.reason.label', 'Reason')}
      value={value}
      onChange={(event) => onChange(event.target.value)}
      error={error !== null}
      helperText={error ?? t('ledger.reason.help', 'Recorded in the audit log.')}
      slotProps={{ htmlInput: { maxLength: REASON_MAX } }}
    />
  );
}

interface BaseProps {
  open: boolean;
  payeeRef: string;
  /** The partner's name, as the page shows it. */
  name: string;
  onClose: () => void;
  /** The action succeeded: the caller toasts and re-reads. */
  onDone: () => void;
}

/** Hold for cause… (`POST …/ledger/holds {reason}`): one open FOR_CAUSE hold per payee. */
export function HoldForCauseDialog({ open, payeeRef, name, onClose, onDone }: BaseProps) {
  const { t } = useCopy();
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      {open ? (
        <ReasonForm
          title={t('ledger.hold.title', "Hold {{name}}'s payouts for cause?", { name })}
          words={t(
            'ledger.hold.words',
            'No payout runs while the hold is open, and statements that close meanwhile are held. Release it when the cause is resolved.',
          )}
          confirmLabel={t('ledger.hold.confirm', 'Hold payouts')}
          failed={t('ledger.hold.failed', "Couldn't open the hold.")}
          onCancel={onClose}
          onSubmit={async (reason) => {
            await adminApi.openLedgerHold(payeeRef, { reason });
            onDone();
          }}
        />
      ) : null}
    </Dialog>
  );
}

/** Release… on one open hold. 409 `HOLD_OPS_ONLY` and an over-cap refusal are said in words. */
export function ReleaseLedgerHoldDialog({
  hold,
  payeeRef,
  name,
  onClose,
  onDone,
}: Omit<BaseProps, 'open'> & { hold: LedgerHoldView | null }) {
  const { t } = useCopy();
  return (
    <Dialog open={hold !== null} onClose={onClose} fullWidth maxWidth="sm">
      {hold !== null ? (
        <ReasonForm
          title={(() => {
            const [before = '', after = ''] = t(
              'ledger.release.title',
              "Release the {{kind}} hold on {{name}}'s payouts?",
              { kind: KIND_TOKEN, name },
            ).split(KIND_TOKEN);
            return (
              <>
                {before}
                <HoldKindName hold={hold} />
                {after}
              </>
            );
          })()}
          words={t(
            'ledger.release.words',
            'Payouts the hold was stopping can run again at the next payout.',
          )}
          confirmLabel={t('ledger.release.confirm', 'Release hold')}
          failed={t('ledger.release.failed', "Couldn't release the hold.")}
          refusal={(error) => {
            const code = error instanceof ApiError ? String(error.code) : '';
            if (code === 'HOLD_OPS_ONLY') {
              return t(
                'ledger.release.opsOnly',
                "This hold can only be released by operations; it's queued.",
              );
            }
            if (code === 'HOLD_NOT_RELEASABLE') {
              return t(
                'ledger.release.notReleasable',
                "This hold can't be released here: a review over the transfer cap is released by operations, and a tax ID hold clears itself once the tax ID is verified.",
              );
            }
            return null;
          }}
          onCancel={onClose}
          onSubmit={async (reason) => {
            if (typeof hold.holdId !== 'string' || hold.holdId === '') return;
            await adminApi.releaseLedgerHold(payeeRef, hold.holdId, { reason });
            onDone();
          }}
        />
      ) : null}
    </Dialog>
  );
}

interface ReasonFormProps {
  title: ReactNode;
  words: string;
  confirmLabel: string;
  failed: string;
  /** A refusal this action words itself; `null` falls to the shared words. */
  refusal?: (error: unknown) => string | null;
  onCancel: () => void;
  onSubmit: (reason: string) => Promise<void>;
}

function ReasonForm({
  title,
  words,
  confirmLabel,
  failed,
  refusal,
  onCancel,
  onSubmit,
}: ReasonFormProps) {
  const { t } = useCopy();
  const [reason, setReason] = useState('');
  const [fieldError, setFieldError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const problem = reasonProblem(reason, t);
    setFieldError(problem);
    if (problem !== null) return;
    setFormError(null);
    setBusy(true);
    try {
      await onSubmit(reason.trim());
    } catch (error: unknown) {
      setFormError(refusal?.(error) ?? actionError(error, t, failed));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>{words}</DialogContentText>
        {formError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        ) : null}
        <ReasonField
          value={reason}
          onChange={(value) => {
            setReason(value);
            setFieldError(null);
          }}
          error={fieldError}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {confirmLabel}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

/**
 * Void… on an open FOR_CAUSE hold: destructive. The person types the partner's name and a reason;
 * the typed name is sent as `confirmation` (tmm-api compares it, as erasure does).
 */
export function VoidLedgerHoldDialog({
  hold,
  payeeRef,
  name,
  onClose,
  onDone,
}: Omit<BaseProps, 'open'> & { hold: LedgerHoldView | null }) {
  return (
    <Dialog open={hold !== null} onClose={onClose} fullWidth maxWidth="sm">
      {hold !== null ? (
        <VoidForm hold={hold} payeeRef={payeeRef} name={name} onClose={onClose} onDone={onDone} />
      ) : null}
    </Dialog>
  );
}

function VoidForm({
  hold,
  payeeRef,
  name,
  onClose,
  onDone,
}: Omit<BaseProps, 'open'> & { hold: LedgerHoldView }) {
  const { t } = useCopy();
  const [typed, setTyped] = useState('');
  const [reason, setReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // The typed name must read as the name shown (case and outer spaces aside); tmm-api checks it too.
  const named = typed.trim().toLowerCase() === name.trim().toLowerCase();

  const submit = async () => {
    const problem = reasonProblem(reason, t);
    setReasonError(problem);
    if (problem !== null || !named) return;
    if (typeof hold.holdId !== 'string' || hold.holdId === '') return;
    setFormError(null);
    setBusy(true);
    try {
      await adminApi.voidLedgerHold(payeeRef, hold.holdId, {
        reason: reason.trim(),
        confirmation: typed.trim(),
      });
      onDone();
    } catch (error: unknown) {
      setFormError(
        error instanceof ApiError && String(error.code) === 'CONFIRMATION_REQUIRED'
          ? t('ledger.void.mismatch', "The name doesn't match. Type the partner's name as shown.")
          : actionError(error, t, t('ledger.void.failed', "Couldn't void the balance.")),
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>{t('ledger.void.title', "Void {{name}}'s balance?", { name })}</DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'ledger.void.words',
            "The partner's positive balance is voided and will not be paid. Lines already paid are not touched. This can't be undone. The hold stays open until you release it.",
          )}
        </DialogContentText>
        {formError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        ) : null}
        <Stack spacing={2}>
          <TextField
            fullWidth
            required
            label={t('ledger.void.typeName', 'Type {{name}} to confirm', { name })}
            value={typed}
            onChange={(event) => setTyped(event.target.value)}
            autoComplete="off"
          />
          <ReasonField
            value={reason}
            onChange={(value) => {
              setReason(value);
              setReasonError(null);
            }}
            error={reasonError}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} color="error" disabled={!named} onClick={() => void submit()}>
          {t('ledger.void.confirm', 'Void balance')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

interface ManualPaymentProps extends BaseProps {
  /** The statements the panel has read, newest first. */
  statements: LedgerStatementView[];
  /** The default: the latest statement (the summary's). */
  latest: LedgerStatementView;
  /** The balance as sent, shown for reference only. Absent is not zero. */
  balanceMinor?: number | undefined;
  balanceCurrency?: string | undefined;
}

/** Record a manual payment… against a statement (`MANUAL` rail). */
export function ManualPaymentDialog(props: ManualPaymentProps) {
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      {props.open ? <ManualPaymentForm {...props} /> : null}
    </Dialog>
  );
}

type PaymentField = 'statementId' | 'amountMinor' | 'paidOn' | 'reference';

function ManualPaymentForm({
  payeeRef,
  name,
  statements,
  latest,
  balanceMinor,
  balanceCurrency,
  onClose,
  onDone,
}: ManualPaymentProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const locale = i18n.language;
  const statementLabelId = useId();
  const choices = statements.some((s) => s.statementId === latest.statementId)
    ? statements
    : [latest, ...statements];
  const [statementId, setStatementId] = useState(
    typeof latest.statementId === 'string' ? latest.statementId : '',
  );
  const statement = choices.find((s) => s.statementId === statementId) ?? latest;
  // A blank, missing, or null currency is not a code: do not scale the typed amount or post it.
  const currency =
    typeof statement.currency === 'string' && statement.currency !== ''
      ? statement.currency
      : undefined;
  const addressedStatementId =
    typeof statement.statementId === 'string' && statement.statementId !== ''
      ? statement.statementId
      : undefined;
  const [amount, setAmount] = useState('');
  const [paidOn, setPaidOn] = useState('');
  const [reference, setReference] = useState('');
  const [errors, setErrors] = useState<Partial<Record<PaymentField, string>>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const amountMinor = currency !== undefined ? exactMinorFromMajor(amount, currency) : null;
    const next: Partial<Record<PaymentField, string>> = {};
    if (amountMinor === null) {
      next.amountMinor = t(
        'ledger.amount.invalid',
        'Enter an amount above zero, with no more decimals than the currency has.',
      );
    }
    if (paidOn === '')
      next.paidOn = t('ledger.payment.paidOnRequired', 'Enter the date it was paid.');
    const ref = reference.trim();
    if (ref === '')
      next.reference = t('ledger.payment.referenceRequired', 'Enter the payment reference.');
    setErrors(next);
    if (
      Object.keys(next).length > 0 ||
      amountMinor === null ||
      currency === undefined ||
      addressedStatementId === undefined
    ) {
      return;
    }
    setFormError(null);
    setBusy(true);
    try {
      await adminApi.recordLedgerManualPayment(payeeRef, {
        statementId: addressedStatementId,
        amountMinor,
        currency,
        paidOn,
        reference: ref,
      });
      onDone();
    } catch (error: unknown) {
      const code = error instanceof ApiError ? String(error.code) : '';
      if (code === 'STATEMENT_NOT_PAYABLE') {
        // The server's bound, by field. Its payable amount, when it sends one, is shown as sent.
        const payable =
          error instanceof ApiError ? error.details.backend?.['payableMinor'] : undefined;
        setErrors({
          amountMinor:
            typeof payable === 'number' && currency !== undefined
              ? t(
                  'ledger.payment.payableIs',
                  'The payable amount on this statement is {{amount}}.',
                  {
                    amount: formatMoney(payable, currency, locale),
                  },
                )
              : t(
                  'ledger.payment.notPayable',
                  "This amount can't be paid on this statement. It can be at most the latest closing or the balance, whichever is lower.",
                ),
        });
      } else if (isValidationFailed(error)) {
        const fields = validationFields(error as ApiError, t);
        const mine: Partial<Record<PaymentField, string>> = {};
        for (const key of ['statementId', 'amountMinor', 'paidOn', 'reference'] as const) {
          if (fields[key] !== undefined) mine[key] = fields[key];
        }
        if (Object.keys(mine).length > 0) setErrors(mine);
        else
          setFormError(
            errorCopy(error, t, t('ledger.payment.failed', "Couldn't record the payment.")),
          );
      } else {
        setFormError(
          actionError(error, t, t('ledger.payment.failed', "Couldn't record the payment.")),
        );
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <DialogTitle>
        {t('ledger.payment.title', 'Record a manual payment to {{name}}', { name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'ledger.payment.words',
            'Record a payment made outside pay-api. The statement is marked paid externally.',
          )}
        </DialogContentText>
        {formError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {formError}
          </Alert>
        ) : null}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <FormControl fullWidth error={errors.statementId !== undefined}>
            <InputLabel id={statementLabelId}>
              {t('ledger.payment.statement', 'Statement')}
            </InputLabel>
            <Select
              labelId={statementLabelId}
              label={t('ledger.payment.statement', 'Statement')}
              value={statementId}
              onChange={(event) => setStatementId(event.target.value)}
            >
              {choices.map((s, index) => {
                const id =
                  typeof s.statementId === 'string' && s.statementId !== ''
                    ? s.statementId
                    : undefined;
                return id !== undefined ? (
                  <MenuItem key={id} value={id}>
                    {periodLabel(s.periodMonth, locale)}
                  </MenuItem>
                ) : (
                  <MenuItem key={`statement-${index}`} value="" disabled>
                    {periodLabel(s.periodMonth, locale)}
                  </MenuItem>
                );
              })}
            </Select>
            {errors.statementId ? <FormHelperText>{errors.statementId}</FormHelperText> : null}
          </FormControl>
          <Typography variant="body2" color="text.secondary">
            {t('ledger.payment.reference', 'For reference: closing')}{' '}
            <LedgerMoney minor={statement.closingMinor} currency={currency} />
            {' · '}
            {t('ledger.payment.balance', 'balance')}{' '}
            <LedgerMoney minor={balanceMinor} currency={balanceCurrency} />
          </Typography>
          <Stack direction="row" spacing={2}>
            <TextField
              required
              label={t('ledger.amount.label', 'Amount')}
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setErrors((current) => without(current, 'amountMinor'));
              }}
              error={errors.amountMinor !== undefined}
              helperText={errors.amountMinor ?? ' '}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('ledger.currency', 'Currency')}
              value={currency ?? ''}
              slotProps={{ htmlInput: { readOnly: true } }}
              helperText=" "
              sx={{ width: 120 }}
            />
          </Stack>
          <TextField
            required
            type="date"
            label={t('ledger.payment.paidOn', 'Paid on')}
            value={paidOn}
            onChange={(event) => {
              setPaidOn(event.target.value);
              setErrors((current) => without(current, 'paidOn'));
            }}
            error={errors.paidOn !== undefined}
            helperText={errors.paidOn ?? ' '}
            slotProps={{ inputLabel: { shrink: true } }}
          />
          <TextField
            required
            label={t('ledger.payment.referenceLabel', 'Payment reference')}
            value={reference}
            onChange={(event) => {
              setReference(event.target.value);
              setErrors((current) => without(current, 'reference'));
            }}
            error={errors.reference !== undefined}
            helperText={errors.reference ?? ' '}
            slotProps={{ htmlInput: { maxLength: REFERENCE_MAX } }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          {t('common.cancel', 'Cancel')}
        </Button>
        <SubmitButton busy={busy} onClick={() => void submit()}>
          {t('ledger.payment.confirm', 'Record payment')}
        </SubmitButton>
      </DialogActions>
    </>
  );
}

interface AdjustmentProps extends BaseProps {
  /** The payee's currency. Absent: the dialog does not invent one and does not post. */
  currency?: string | undefined;
}

/** Post an adjustment…: a signed amount, its direction chosen in words, then a confirm restating it. */
export function AdjustmentDialog(props: AdjustmentProps) {
  return (
    <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm">
      {props.open ? <AdjustmentForm {...props} /> : null}
    </Dialog>
  );
}

type Direction = 'credit' | 'charge';
type AdjustmentField = 'direction' | 'amount' | 'reason';

function AdjustmentForm({ payeeRef, name, currency, onClose, onDone }: AdjustmentProps) {
  const { t } = useCopy();
  const { i18n } = useTranslation();
  const directionLabelId = useId();
  // No direction is chosen for the person: the sign is theirs to pick.
  const [direction, setDirection] = useState<Direction | null>(null);
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<Partial<Record<AdjustmentField, string>>>({});
  const [confirming, setConfirming] = useState<{ minor: number; direction: Direction } | null>(
    null,
  );
  const [formError, setFormError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const payeeCurrency = typeof currency === 'string' && currency !== '' ? currency : undefined;

  const review = () => {
    const minor = payeeCurrency !== undefined ? exactMinorFromMajor(amount, payeeCurrency) : null;
    const next: Partial<Record<AdjustmentField, string>> = {};
    if (direction === null) {
      next.direction = t('ledger.adjustment.directionRequired', 'Choose credit or charge.');
    }
    if (minor === null) {
      next.amount = t(
        'ledger.amount.invalid',
        'Enter an amount above zero, with no more decimals than the currency has.',
      );
    }
    const problem = reasonProblem(reason, t);
    if (problem !== null) next.reason = problem;
    setErrors(next);
    if (Object.keys(next).length > 0 || minor === null || direction === null) return;
    setFormError(null);
    setConfirming({ minor, direction });
  };

  const post = async () => {
    if (confirming === null || payeeCurrency === undefined) return;
    setFormError(null);
    setBusy(true);
    try {
      await adminApi.postLedgerAdjustment(payeeRef, {
        // The sign is the direction the person chose: a charge takes from the partner's balance.
        amountMinor: confirming.direction === 'charge' ? -confirming.minor : confirming.minor,
        currency: payeeCurrency,
        reason: reason.trim(),
      });
      onDone();
    } catch (error: unknown) {
      setFormError(
        actionError(error, t, t('ledger.adjustment.failed', "Couldn't post the adjustment.")),
      );
    } finally {
      setBusy(false);
    }
  };

  if (confirming !== null && payeeCurrency !== undefined) {
    const shown = formatMoney(confirming.minor, payeeCurrency, i18n.language);
    return (
      <>
        <DialogTitle>
          {confirming.direction === 'credit'
            ? t('ledger.adjustment.confirmCreditTitle', 'Credit {{name}} {{amount}}?', {
                name,
                amount: shown,
              })
            : t('ledger.adjustment.confirmChargeTitle', 'Charge {{name}} {{amount}}?', {
                name,
                amount: shown,
              })}
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {confirming.direction === 'credit'
              ? t(
                  'ledger.adjustment.confirmCreditWords',
                  "This adds {{amount}} to {{name}}'s balance. It's posted as an adjustment and audited.",
                  { name, amount: shown },
                )
              : t(
                  'ledger.adjustment.confirmChargeWords',
                  "This takes {{amount}} from {{name}}'s balance. It's posted as an adjustment and audited.",
                  { name, amount: shown },
                )}
          </DialogContentText>
          {formError ? <Alert severity="error">{formError}</Alert> : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(null)} disabled={busy}>
            {t('ledger.adjustment.back', 'Back')}
          </Button>
          <SubmitButton busy={busy} onClick={() => void post()}>
            {t('ledger.adjustment.post', 'Post adjustment')}
          </SubmitButton>
        </DialogActions>
      </>
    );
  }

  return (
    <>
      <DialogTitle>
        {t('ledger.adjustment.title', 'Post an adjustment for {{name}}', { name })}
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {t(
            'ledger.adjustment.words',
            "An adjustment corrects the partner's balance. It replaces waiving a charge and marking one collected.",
          )}
        </DialogContentText>
        <Stack spacing={2}>
          <FormControl error={errors.direction !== undefined} required>
            <FormLabel id={directionLabelId}>
              {t('ledger.adjustment.direction', 'Direction')}
            </FormLabel>
            <RadioGroup
              aria-labelledby={directionLabelId}
              value={direction ?? ''}
              onChange={(event) => {
                setDirection(event.target.value as Direction);
                setErrors((current) => without(current, 'direction'));
              }}
            >
              <FormControlLabel
                value="credit"
                control={<Radio />}
                label={t('ledger.adjustment.credit', 'Credit the partner (adds to their balance)')}
              />
              <FormControlLabel
                value="charge"
                control={<Radio />}
                label={t(
                  'ledger.adjustment.charge',
                  'Charge the partner (takes from their balance)',
                )}
              />
            </RadioGroup>
            {errors.direction ? <FormHelperText>{errors.direction}</FormHelperText> : null}
          </FormControl>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              required
              label={t('ledger.amount.label', 'Amount')}
              value={amount}
              onChange={(event) => {
                setAmount(event.target.value);
                setErrors((current) => without(current, 'amount'));
              }}
              error={errors.amount !== undefined}
              helperText={errors.amount ?? ' '}
              slotProps={{ htmlInput: { inputMode: 'decimal' } }}
              sx={{ flex: 1 }}
            />
            <TextField
              label={t('ledger.currency', 'Currency')}
              value={payeeCurrency ?? ''}
              slotProps={{ htmlInput: { readOnly: true } }}
              helperText=" "
              sx={{ width: 120 }}
            />
          </Box>
          <ReasonField
            value={reason}
            onChange={(value) => {
              setReason(value);
              setErrors((current) => without(current, 'reason'));
            }}
            error={errors.reason ?? null}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('common.cancel', 'Cancel')}</Button>
        <Button variant="contained" onClick={review}>
          {t('ledger.adjustment.continue', 'Continue')}
        </Button>
      </DialogActions>
    </>
  );
}
